// src/pages/SuperAdminTables.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../styles/SuperAdminTables.css';
import { FaSortUp, FaSortDown, FaSearch } from 'react-icons/fa'; // ✅ Import ikon pencarian

// Konfigurasi tabel dan ID kolom kunci utama
const tableConfigs = [
    { name: 'Accounts', endpoint: 'accounts', idColumn: 'id' },
    { name: 'Tanks', endpoint: 'tanks', idColumn: 'id' },
    { name: 'Sensors', endpoint: 'sensors', idColumn: 'sensor_id' },
    { name: 'Water Usage', endpoint: 'water_usage', idColumn: 'id' },
    { name: 'Tank Level', endpoint: 'tank_level', idColumn: 'id' },
    { name: 'Notifications', endpoint: 'notifications', idColumn: 'id' },
    { name: 'Sales', endpoint: 'sales', idColumn: 'id' },
    { name: 'Pending Registrations', endpoint: 'pending_registrations', idColumn: 'id' },
    { name: 'Sebara Depot Air Minum', endpoint: 'sebara_depot_air_minum', idColumn: 'id' },
];

const SuperAdminTables = () => {
    const [selectedTable, setSelectedTable] = useState(tableConfigs[0]);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [editingRow, setEditingRow] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [totalItems, setTotalItems] = useState(0);

    const [sortColumn, setSortColumn] = useState(null);
    const [sortOrder, setSortOrder] = useState('ASC');

    // ✅ State baru untuk Pencarian
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(''); // Untuk debounce pencarian

    const token = localStorage.getItem('token');

    const fetchTableData = useCallback(async () => {
        setLoading(true);
        setError('');
        setMessage('');
        try {
            const offset = (currentPage - 1) * itemsPerPage;
            let url = `https://depotaircerdas.com/api/superadmin/tables/${selectedTable.endpoint}?_limit=${itemsPerPage}&_offset=${offset}`;

            if (sortColumn) {
                url += `&_sort=${sortColumn}&_order=${sortOrder}`;
            }
            // ✅ Tambahkan parameter pencarian jika ada
            if (debouncedSearchQuery) {
                url += `&_q=${encodeURIComponent(debouncedSearchQuery)}`;
            }

            console.log("Fetching data with URL:", url);
            console.log("Current Page for Fetch:", currentPage);

            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const totalCount = parseInt(response.headers['x-total-count'] || '0', 10);
            setTotalItems(totalCount);
            console.log("Response X-Total-Count:", totalCount);

            if (Array.isArray(response.data)) {
                setTableData(response.data);
            } else {
                setError("Format data yang diterima tidak sesuai.");
                setTableData([]);
            }
        } catch (err) {
            console.error(`Error fetching ${selectedTable.name} data:`, err);
            setError(err.response?.data?.message || `Gagal mengambil data ${selectedTable.name}.`);
            setTableData([]);
        } finally {
            setLoading(false);
        }
    }, [selectedTable, token, currentPage, itemsPerPage, sortColumn, sortOrder, debouncedSearchQuery]); // ✅ Tambahkan debouncedSearchQuery

    useEffect(() => {
        if (token && selectedTable) {
            fetchTableData();
        }
    }, [token, selectedTable, fetchTableData]);

    // ✅ Debounce useEffect untuk search query
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500); // Debounce 500ms

        return () => {
            clearTimeout(handler);
        };
    }, [searchQuery]); // Hanya jalankan saat searchQuery berubah

    // ✅ Reset halaman ke 1 saat search query berubah (debounced)
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchQuery]);

    const handleEditClick = (row) => {
        setEditingRow(row[selectedTable.idColumn]);
        setEditFormData({ ...row });
    };

    const handleCancelEdit = () => {
        setEditingRow(null);
        setEditFormData({});
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleUpdateSubmit = async () => {
        setLoading(true);
        setError('');
        setMessage('');
        try {
            const dataToSend = { ...editFormData };
            delete dataToSend[selectedTable.idColumn];
            
            for (const key in dataToSend) {
                if (dataToSend.hasOwnProperty(key)) {
                    if (['capacity', 'height_cm', 'volume', 'gallons', 'id', 'water_level_cm', 'price', 'quantity'].includes(key) && !isNaN(parseFloat(dataToSend[key]))) {
                        dataToSend[key] = parseFloat(dataToSend[key]);
                    }
                    if (['is_super_admin', 'is_main', 'is_registered'].includes(key)) {
                        dataToSend[key] = Boolean(dataToSend[key]) ? 1 : 0;
                    }
                    if (key === 'password' && dataToSend[key] === '') {
                        delete dataToSend[key];
                    }
                }
            }
            
            const response = await axios.put(
                `https://depotaircerdas.com/api/superadmin/tables/${selectedTable.endpoint}/${editingRow}`,
                dataToSend,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            setMessage(response.data.message);
            setEditingRow(null);
            setEditFormData({});
            fetchTableData();
        } catch (err) {
            console.error("Error updating data:", err);
            setError(err.response?.data?.message || "Gagal memperbarui data.");
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const handlePageChange = (page) => {
        console.log("Attempting to change page from", currentPage, "to", page);
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleItemsPerPageChange = (e) => {
        const newItemsPerPage = parseInt(e.target.value, 10);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortOrder('ASC');
        }
        setCurrentPage(1);
    };

    // ✅ Handler untuk input pencarian
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const renderTable = () => {
        if (!tableData || tableData.length === 0) {
            return <p>Tidak ada data untuk tabel ini.</p>;
        }

        const columns = Object.keys(tableData[0]);

        return (
            <div className="table-responsive">
                <table>
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <th key={col} onClick={() => handleSort(col)} className="sortable-header">
                                    {col}
                                    {sortColumn === col && (
                                        sortOrder === 'ASC' ? <FaSortUp className="sort-icon" /> : <FaSortDown className="sort-icon" />
                                    )}
                                </th>
                            ))}
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map(row => (
                            <tr key={row[selectedTable.idColumn]}>
                                {columns.map(col => (
                                    <td key={col}>
                                        {editingRow === row[selectedTable.idColumn] ? (
                                            <input
                                                type={
                                                    (col.includes('password') || col.includes('token')) ? 'password' :
                                                    (col.includes('is_super_admin') || col.includes('is_main') || col.includes('is_registered')) ? 'checkbox' :
                                                    (col.includes('capacity') || col.includes('height_cm') || col.includes('volume') || col.includes('gallons') || col.includes('price') || col.includes('quantity')) ? 'number' :
                                                    'text'
                                                }
                                                name={col}
                                                value={
                                                    (col.includes('is_super_admin') || col.includes('is_main') || col.includes('is_registered')) ? undefined :
                                                    (editFormData[col] !== null && editFormData[col] !== undefined) ? editFormData[col] : ''
                                                }
                                                checked={
                                                    (col.includes('is_super_admin') || col.includes('is_main') || col.includes('is_registered')) ? Boolean(editFormData[col]) : undefined
                                                }
                                                onChange={handleFormChange}
                                                disabled={col === selectedTable.idColumn}
                                            />
                                        ) : (
                                            (col.includes('is_super_admin') || col.includes('is_main') || col.includes('is_registered')) ? (
                                                row[col] === 1 ? 'True (1)' : 'False (0)'
                                            ) : (col.includes('timestamp') || col.includes('requested_at') || col.includes('last_online') || col.includes('last_offline_notification_at') || col.includes('sent_at')) && row[col] ? new Date(row[col]).toLocaleString() : (
                                                row[col] === null ? 'NULL' : row[col]
                                            )
                                        )}
                                    </td>
                                ))}
                                <td>
                                    {editingRow === row[selectedTable.idColumn] ? (
                                        <>
                                            <button className="btn-save" onClick={handleUpdateSubmit} disabled={loading}>Simpan</button>
                                            <button className="btn-cancel" onClick={handleCancelEdit} disabled={loading}>Batal</button>
                                        </>
                                    ) : (
                                        <button className="btn-edit" onClick={() => handleEditClick(row)}>Edit</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="pagination-controls">
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || loading}>Previous</button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || loading}>Next</button>
                    <label htmlFor="items-per-page" style={{ marginLeft: '20px' }}>Items per page:</label>
                    <select id="items-per-page" value={itemsPerPage} onChange={handleItemsPerPageChange} disabled={loading}>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                    </select>
                </div>
            </div>
        );
    };

    return (
        <div className="superadmin-tables-container">
            <h2>Kelola Data Master</h2>
            <div className="controls-row"> {/* ✅ Kontainer baru untuk select dan search */}
                <div className="table-selector">
                    <label htmlFor="select-table">Pilih Tabel:</label>
                    <select id="select-table" onChange={(e) => setSelectedTable(tableConfigs.find(config => config.endpoint === e.target.value))}>
                        {tableConfigs.map(config => (
                            <option key={config.endpoint} value={config.endpoint}>{config.name}</option>
                        ))}
                    </select>
                </div>
                {/* ✅ Search Input */}
                <div className="search-input-container">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Cari..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        disabled={loading}
                    />
                </div>
            </div>

            {loading && <p>Memuat data...</p>}
            {error && <p className="error-message">{error}</p>}
            {message && <p className="success-message">{message}</p>}

            {!loading && renderTable()}
        </div>
    );
};

export default SuperAdminTables;